<template>
  <obroci-table :data="data" :loading="isLoading" :search-query="searchQuery" />
</template>

<script>
import ObrociTable from "@/components/guest/ObrociTable.vue";
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import { joinPrilog } from "@/utils/guestPrilog";

export default {
  name: "ObrociTab",
  mixins: [hasPrinting, hasExcel],
  components: {
    ObrociTable,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    isActiveTab: Boolean,
  },
  data() {
    return {
      data: [],
      isLoading: false,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("food"), value: "name", sortable: false },
        {
          text: this.$t("quantity"),
          value: "quantity",
          sortable: false,
          align: "center",
        },
      ];
    },
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },
    totalQuantity() {
      return this.data.reduce((sum, item) => sum + item.quantity, 0);
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (!isPrinting || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.printObrociTable();
    },
    isExcelDownloading(isDownloading) {
      if (!isDownloading || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.exportObrociExcel();
    },
    dateRange: {
      handler() {
        this.loadData();
      },
      immediate: true,
    },
  },
  methods: {
    async loadData() {
      if (!this.restaurantId || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
        };

        const response = await reportService.getRestaurantGuest(
          this.restaurantId,
          params
        );
        this.processData(response.data);
      } catch (error) {
        console.error("Error loading meals data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    processData(data) {
      const mealsMap = {};

      // Iterate through all shifts
      Object.keys(data).forEach((shift) => {
        const shiftData = data[shift];

        // Iterate through all meals in the shift
        Object.keys(shiftData).forEach((mealName) => {
          const mealData = shiftData[mealName];

          // Get prilog text from the prilog array
          const prilogText = joinPrilog(mealData.prilog);

          if (mealsMap[mealName]) {
            // Meal already exists, add quantity
            mealsMap[mealName].quantity += mealData.quantity;
            // Merge prilog if different
            if (prilogText && !mealsMap[mealName].prilog.includes(prilogText)) {
              mealsMap[mealName].prilog = mealsMap[mealName].prilog
                ? `${mealsMap[mealName].prilog}, ${prilogText}`
                : prilogText;
            }
          } else {
            // New meal, add to map
            mealsMap[mealName] = {
              name: mealName,
              quantity: mealData.quantity,
              id: mealData.id,
              prilog: prilogText,
            };
          }
        });
      });

      // Convert map to array and sort by name
      this.data = Object.values(mealsMap).sort((a, b) => a.name.localeCompare(b.name));
    },
    printObrociTable() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        name: this.toLatin(item.name),
        quantity: item.quantity,
      }));

      // Add total row
      tableItems.push({
        num: "",
        name: `<strong>${this.$t("total").toUpperCase()}</strong>`,
        quantity: `<strong>${this.totalQuantity}</strong>`,
      });

      this.printDocument({
        pageTitle: this.$t("meals"),
        title: this.$t("meals"),
        subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
          this.dateRange[1]
        )}`,
        tables: [
          {
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
    exportObrociExcel() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        name: this.toLatin(item.name),
        quantity: item.quantity,
      }));

      // Add total row
      tableItems.push({
        num: "",
        name: this.$t("total").toUpperCase(),
        quantity: this.totalQuantity,
      });

      this.downloadExcel({
        fileName: `obroci_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t("meals"),
            title: this.$t("meals"),
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )}`,
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
  },
};
</script>
